import { supabase } from './supabaseClient';

/**
 * Service for handling user profile operations
 */

/**
 * Get user profile by user ID
 * @param {string} userId - User's UUID
 * @returns {Promise} - User profile data
 */
export const getProfile = async (userId) => {
  try {
    if (!userId) {
      console.error('Error getting user: Auth session missing!');
      return { profile: null, error: new Error('Auth session missing!') };
    }
    
    console.log('Getting profile for user ID:', userId);
    
    // Query using the uuid column which directly matches the auth user's UUID
    // Don't use .single() to avoid the "JSON object requested, multiple (or no) rows returned" error
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('uuid', userId);
    
    if (error) {
      console.error('Supabase error details:', JSON.stringify(error));
      throw error;
    }
    
    // Check if any profile was found
    if (!data || data.length === 0) {
      console.log('No profile found for user ID:', userId);
      // Return null profile without error to allow creating a new profile
      return { profile: null, error: null };
    }
    
    // Return the first profile found
    return { profile: data[0], error: null };
  } catch (error) {
    console.error('Error fetching profile:', error.message);
    return { profile: null, error };
  }
};

/**
 * Create or update user profile
 * @param {string} userId - User's UUID
 * @param {object} profileData - Profile data to update
 * @returns {Promise} - Supabase response
 */
export const updateProfile = async (userId, profileData) => {
  try {
    if (!userId) {
      console.error('Error updating user: Auth session missing!');
      return { data: null, error: new Error('Auth session missing!') };
    }
    
    // Add updated_at timestamp
    profileData.updated_at = new Date().toISOString();
    
    console.log('Updating profile for user ID:', userId);
    
    // First check if profile exists
    const { data: existingProfiles, error: checkError } = await supabase
      .from('profiles')
      .select('id, uuid')
      .eq('uuid', userId);
    
    if (checkError) {
      console.error('Error checking if profile exists:', checkError);
      throw checkError;
    }
    
    let result;
    
    if (existingProfiles && existingProfiles.length > 0) {
      // Update existing profile
      const existingProfile = existingProfiles[0];
      console.log('Updating existing profile with ID:', existingProfile.id);
      result = await supabase
        .from('profiles')
        .update(profileData)
        .eq('uuid', userId)
        .select();
    } else {
      // Insert new profile
      console.log('Creating new profile for user:', userId);
      // Include the uuid in the insert
      result = await supabase
        .from('profiles')
        .insert({ 
          uuid: userId, 
          ...profileData 
        })
        .select();
    }
    
    const { data, error } = result;
    
    if (error) {
      console.error('Supabase error details:', JSON.stringify(error));
      throw error;
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Error creating/updating user profile:', error.message);
    return { data: null, error };
  }
};

/**
 * Upload profile image to Supabase Storage
 * @param {string} userId - User's UUID
 * @param {File} file - Image file to upload
 * @returns {Promise} - Supabase storage response with URL
 */
export const uploadProfileImage = async (userId, file) => {
  try {
    // Check if file is valid
    if (!file || !(file instanceof File || file instanceof Blob)) {
      console.error('Invalid file object:', file);
      return { url: null, error: new Error('Invalid file object provided') };
    }

    // Check file size - limit to 5MB
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      console.error('File too large:', file.size, 'bytes');
      return { url: null, error: new Error(`File size exceeds 5MB limit (${Math.round(file.size/1024/1024)}MB)`) };
    }

    // First, create the bucket if it doesn't exist
    try {
      const { error: bucketError } = await supabase.storage.createBucket('profile-images', {
        public: true,
        fileSizeLimit: MAX_FILE_SIZE
      });
      
      if (bucketError && !bucketError.message.includes('already exists')) {
        console.warn('Bucket creation warning:', bucketError.message);
      } else {
        console.log('Bucket check successful');
      }
    } catch (bucketErr) {
      console.warn('Bucket operation warning:', bucketErr.message);
      // Continue even if bucket creation fails (it might already exist)
    }
    
    // Create a unique file path
    const fileExt = file.name ? file.name.split('.').pop() : 'jpg';
    const fileName = `${userId}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;
    
    console.log('Uploading profile image for user:', userId);
    console.log('File path:', filePath);
    console.log('File type:', file.type);
    console.log('File size:', file.size, 'bytes');
    
    // Implement retry logic for upload
    let attempts = 0;
    const maxAttempts = 3;
    let data = null;
    let error = null;
    
    while (attempts < maxAttempts) {
      attempts++;
      console.log(`Upload attempt ${attempts} of ${maxAttempts}...`);
      
      try {
        // Upload the file to Supabase Storage
        const result = await supabase.storage
          .from('profile-images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: true,
            contentType: file.type || 'image/jpeg'
          });
        
        data = result.data;
        error = result.error;
        
        if (!error) {
          console.log('Upload successful on attempt', attempts);
          break; // Exit loop on success
        } else {
          console.warn(`Upload attempt ${attempts} failed:`, error.message);
          
          if (attempts < maxAttempts) {
            // Wait before retrying (exponential backoff)
            const delay = Math.pow(2, attempts) * 1000;
            console.log(`Retrying in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      } catch (uploadErr) {
        console.error('Unexpected error during upload attempt:', uploadErr);
        error = uploadErr;
      }
    }
    
    if (error) {
      console.error('All upload attempts failed:', error);
      throw error;
    }
    
    console.log('File uploaded successfully:', data);
    
    // Get the public URL
    const { data: urlData } = supabase.storage
      .from('profile-images')
      .getPublicUrl(filePath);
    
    const publicUrl = urlData.publicUrl;
    console.log('Generated public URL:', publicUrl);
    
    // Update the user's profile with the new image URL
    try {
      await updateProfile(userId, { profile_image_url: publicUrl });
    } catch (profileUpdateError) {
      console.warn('Profile update with new image URL failed:', profileUpdateError.message);
      // Continue anyway since the image was uploaded successfully
    }
    
    return { url: publicUrl, error: null };
  } catch (error) {
    console.error('Error uploading profile image:', error.message);
    return { url: null, error: new Error('Failed to upload image: ' + (error.message || 'Network error')) };
  }
};

/**
 * Get all profiles with optional filtering
 * @param {object} filters - Optional filters to apply
 * @param {number} page - Page number for pagination
 * @param {number} limit - Number of profiles per page
 * @returns {Promise} - List of profiles
 */
export const getProfiles = async (filters = {}, page = 1, limit = 10) => {
  try {
    let query = supabase
      .from('profiles')
      .select('*');
    
    // Apply filters if provided
    if (filters.userType) {
      query = query.eq('user_type', filters.userType);
    }
    
    if (filters.instruments && filters.instruments.length > 0) {
      // Filter for profiles that have any of the specified instruments
      query = query.contains('instruments', filters.instruments);
    }
    
    if (filters.location) {
      query = query.ilike('location', `%${filters.location}%`);
    }
    
    // Apply pagination
    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.range(from, to);
    
    const { data, error, count } = await query;
    
    if (error) throw error;
    return { profiles: data, count, error: null };
  } catch (error) {
    console.error('Error getting profiles:', error.message);
    return { profiles: [], count: 0, error };
  }
};

/**
 * Delete a user's profile image
 * @param {string} userId - User's UUID
 * @param {string} imageUrl - URL of the image to delete
 * @returns {Promise} - Supabase storage response
 */
export const deleteProfileImage = async (userId, imageUrl) => {
  try {
    // Extract the file path from the URL
    const storageUrl = supabase.storage.from('profile-images').getPublicUrl('').data.publicUrl;
    const filePath = imageUrl.replace(storageUrl, '');
    
    // Delete the file from storage
    const { error } = await supabase.storage
      .from('profile-images')
      .remove([filePath]);
    
    if (error) throw error;
    
    // Update the profile to remove the image URL
    await updateProfile(userId, { profile_image_url: null });
    
    return { error: null };
  } catch (error) {
    console.error('Error deleting profile image:', error.message);
    return { error };
  }
};
